/*
 * Copyright © 2025. Legalbird eine Marke der Legalbird GmbH
 */

import React, { useEffect, useRef, useState } from "react";

import { Helmet } from "react-helmet-async";
import RecordingConsentForm from "@/src/components/Form/Common/RecordingConsent/RecordingConsentForm";
import RecordingConsentSuccess from "@/src/components/Form/Common/RecordingConsent/RecordingConsentSuccess";
import useProductParams from "@/src/hooks/useProductParams";
import { getProductData, getProductNameBy } from "@/src/services/productService";
import { useQuery } from "@tanstack/react-query";
import { fetchResource, queryKeys } from "@/src/services/reactQuery/reactQueryService";
import LegalbirdLoader from "@/src/components/ContentLoader/LegalbirdLoader";
import { Box } from "@mui/material";
import LayoutProtectedRoutes from "@/src/containers/Layouts/LayoutProtectedRoutes";

export default function RecordingConsentPage() {
  const [isSuccess, setIsSuccess] = useState(false);
  const successRef = useRef<HTMLDivElement | null>(null);
  const { productName, productId } = useProductParams();
  const productClassName = getProductNameBy("urlPartShort", productName);
  const productApiUri = getProductData(productClassName, "apiUri");
  useEffect(() => {
    if (!isSuccess) {
      return;
    }
    requestAnimationFrame(() => {
      successRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, [isSuccess]);

  const hasProduct = Boolean(productApiUri && productId);

  const { data: product, isLoading: isLoadingProduct } = useQuery({
    queryKey: hasProduct ? queryKeys.item(productApiUri, productId) : ["product", "none"],
    queryFn: () => fetchResource(productApiUri!, productId!),
    enabled: hasProduct,
  });

  const handleConsent = async () => {
    setIsSuccess(true);
  };

  if (hasProduct && isLoadingProduct) {
    return (
      <LayoutProtectedRoutes>
        <LegalbirdLoader />
      </LayoutProtectedRoutes>
    );
  }

  return (
    <LayoutProtectedRoutes>
      <Helmet>
        <title>Einwilligung zur Aufzeichnung & Gesprächszusammenfassung | Legalbird</title>
      </Helmet>
      {!isSuccess && <RecordingConsentForm handleConsent={handleConsent} />}
      {isSuccess && (
        <Box ref={successRef} sx={{ scrollMarginTop: (theme) => theme.spacing(12) }}>
          <RecordingConsentSuccess product={product} />
        </Box>
      )}
    </LayoutProtectedRoutes>
  );
}
